import { fetchOpenAIText } from "../utils/openaiClient";

export function useWordTranslationGeneratorLogic({
  wordTranslationInput, setWordTranslationInput, wordTranslationTargetLanguage,
  setWordTranslationLoading, generatedWordTranslations, setGeneratedWordTranslations,
  currentTab, activeEditor, onInsertToBoard,
  setShowWordTranslationGenerator, setSelectedTemplate,
}) {
  const handleWordTranslationGenerate = async () => {
    if (!wordTranslationInput.trim()) return;

    setWordTranslationLoading(true);
    try {
      const wordCount = wordTranslationInput
        .split(/[\n,]+/)
        .map(w => w.trim())
        .filter(Boolean).length;

      const buildPrompt = (strict = false) => `You are a professional ${wordTranslationTargetLanguage} translator building a word-matching exercise for English language learners.

Below is a list of English words/phrases, one per line or comma-separated. Translate each one to the quality of a professional bilingual learner's dictionary — like the entry you'd write for a serious ${wordTranslationTargetLanguage}-English dictionary, not a quick machine-translation guess.

Hard rules:
- Translate EVERY item in the list — do not skip, merge, or reorder any. The output "pairs" array must have exactly ${wordCount} items, one per input item, in the same order.
- SINGLE WORDS (one word, no spaces) stand ALONE (not inside a sentence): give the BASE / CITATION FORM — verbs in the infinitive (or infinitive-equivalent citation form for the target language), nouns in the singular nominative, adjectives in their neutral/dictionary form. NEVER a conjugated, inflected, or person/tense-specific form.
  Example: English "can" (the modal verb) → Russian "мочь" (infinitive), NOT "могу"/"может"/"можешь".
- MULTI-WORD PHRASES keep their own natural grammatical shape instead of being collapsed to a bare infinitive — translate a gerund phrase as a gerund-ish phrase, a noun phrase as a noun phrase, exactly as a bilingual dictionary would present that entry.
  Example: "callously killing" → "безжалостно убивая" (keeps the gerund), not "убивать" (which would drop "callously" and change the part of speech).
- Two different kinds of phrases need two different translation strategies — tell them apart:
  (a) COMPOSITIONAL phrases (the meaning is just its parts combined, e.g. "malicious gossip" = malicious + gossip, "heinous charge" = heinous + charge): translate each component faithfully and combine naturally. Do NOT swap in a different, more "sophisticated" concept just because it's a loosely related idea — that drifts away from what the source phrase actually says.
    Example: "Malicious gossip" → "злонамеренные сплетни" / "злостные слухи" (stays on "gossip/rumor"). WRONG: "злословие, клевета" (that's "slander/defamation" — a related but different concept, not what "gossip" means).
  (b) TRUE IDIOMS (the meaning is NOT the sum of its parts, e.g. "to follow suit"): translate the meaning/function, not the literal words, using the equivalent idiom or closest natural expression in the target language.
- Depth over terseness: where the word/phrase has two or three closely-related senses or near-synonyms that together capture its real meaning better than any single word could, give them joined with ", " — this is often what makes a translation feel professional rather than thin. Do NOT do this reflexively for every item though: a simple, unambiguous word (e.g. "table", "run") just gets its one natural translation. Reserve the comma-joined form for words/phrases where the extra nuance genuinely earns its place — max 2-3 synonyms, never a sprawling list, and every synonym must still be a translation of the SAME word/phrase (see the compositional-phrase rule above — don't let "richer" turn into "different concept").
  Example: "Infamous" → "печально известный, пользующийся дурной славой" (two facets of the same meaning, both useful). "Table" → "стол" (no need to pad it).
- No parenthetical explanations, examples, or usage notes — only the translation(s) themselves, in the format above.
- Use correct native spelling and script for ${wordTranslationTargetLanguage} (e.g. Cyrillic for Russian, Hanzi for Chinese, Devanagari for Hindi, Hangul for Korean, Arabic script for Arabic) — never a romanized/transliterated version unless that language is conventionally written in Latin script.
- If a single word is ambiguous (multiple unrelated meanings, e.g. "can" the modal verb vs. "can" the metal container), pick whichever single meaning is more common overall for a general-vocabulary word list — do not translate both unrelated senses at once.
- Match register: neutral dictionary register, not slang or overly formal/academic, unless the source word itself is slang or formal.
- Work like a professional lexicographer, not a literal word-for-word converter for idioms, but stay faithful to the actual words for compositional phrases. Avoid stiff "translationese" without drifting into a different concept.
- Before finalizing each pair, silently double-check: (1) for single words, is this really the base/citation form? (2) for phrases, did I keep the natural phrase structure instead of flattening it? (3) if it's a compositional phrase, does every synonym still translate the SAME components, not a related-but-different concept? (4) does the richness (one word vs. 2-3 synonyms) match how much nuance this specific item actually needs? (5) would a native speaker recognize this as natural, correct, dictionary-quality usage? Fix any pair that fails these checks before returning.
${strict ? '- STRICT retry: your previous attempt did not return exactly one clean translation per word — fix that this time.' : ''}

Return ONLY valid JSON in this format:
{
  "title": "Word-Translation Matching Exercise",
  "targetLanguage": "${wordTranslationTargetLanguage}",
  "pairs": [
    {"word": "example", "translation": "пример"},
    {"word": "another", "translation": "другой"}
  ]
}

Words:
${wordTranslationInput}`;

      let jsonData = null;
      for (let attempt = 0; attempt < 2 && !jsonData; attempt++) {
        const content = await fetchOpenAIText({
          prompt: buildPrompt(attempt > 0),
          temperature: 0.4,
          maxTokens: 1200,
        });
        if (!content) continue;
        try {
          const parsed = JSON.parse(content.trim());
          if (Array.isArray(parsed.pairs) && parsed.pairs.length > 0) jsonData = parsed;
        } catch (_) { /* try again below, or give up after 2 attempts */ }
      }

      if (jsonData) {
        setGeneratedWordTranslations(jsonData);
      } else {
        alert('Could not generate translations — the AI response was invalid. Please try again.');
      }
    } catch (e) {
      alert('Error generating translations. Please try again.');
    } finally {
      setWordTranslationLoading(false);
    }
  };

  const handleWordTranslationInsert = () => {
    if (!generatedWordTranslations) return;

    if (currentTab === 0) {
      if (!activeEditor) return;
      activeEditor.chain().focus().insertWordTranslationExercise({
        pairs: generatedWordTranslations.pairs,
        title: generatedWordTranslations.title,
        targetLanguage: generatedWordTranslations.targetLanguage
      }).run();
    } else {
      onInsertToBoard(generatedWordTranslations, 'wordtranslation');
    }

    setShowWordTranslationGenerator(false);
    setWordTranslationInput("");
    setGeneratedWordTranslations(null);
    setSelectedTemplate(null);
  };

  const handleWordTranslationCancel = () => {
    setShowWordTranslationGenerator(false);
    setWordTranslationInput("");
    setGeneratedWordTranslations(null);
    setSelectedTemplate(null);
  };

  return { handleWordTranslationGenerate, handleWordTranslationInsert, handleWordTranslationCancel };
}
